import React from 'react';
import { parseISO } from 'date-fns';
import ReactDataPickerComp from '../ReactDataPicker/ReactDataPickerComp';
import Panel from '../Panel/Panel';
import MandatoryIconField from '../MandatoryIconField';
import InputField from '../Input/InputField';
import ComboBox from '../ComboBox/ComboBox';
import TextAreaField from '../TextAreaField/TextAreaField';
import SearchableComboBox from '../SearchableComboBox/SearchableComboBox';

const AutomaticallyListedAttributes = ({
    list,
    attributeList,
    readOnlyAttributes,
    height = '100%'
}) => {
    if (!list || list.length === 0 || !list[0] || !list[0].constructorList) {
        return;
    }

    const sortedList = list[0].constructorList.sort((a, b) => {
        const presentationSequenceA = a.presentationSequence;
        const presentationSequenceB = b.presentationSequence;
        return presentationSequenceA - presentationSequenceB;
    });

    const mainStyle = {
        flexWrap: 'wrap',
        height: height,
        overflow: 'auto',
        alignContent: 'flex-start',
        padding: '4px'
    };

    const row = {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        flexWrap: 'wrap'
    }

    let rows = [];
    let currentRow = [];

    sortedList.forEach(attribute => {
        const attributeDataBackend = {
            field: attribute.field,
            label: attribute.label,
            isMandatory: attribute.isMandatory,
            columnType: attribute.columnType,
            readOnly: attribute.readOnly,
            isVisible: (attribute.visible === undefined || attribute.visible === null || attribute.visible === 'S') ? true : false,
            mask: attribute.mask,
            placeholder: attribute.placeHolder,
            dataType: attribute.dataType
        };

        if (!attributeDataBackend.label) {
            return;
        }

        const attributeDataFrontend = attributeList.find(obj => obj.name === attributeDataBackend.field) || {};
        const readOnlyField = getReadOnlyField(readOnlyAttributes, attributeDataFrontend.readOnly, attributeDataBackend.readOnly);
        const inputField = getComponentField(attributeDataBackend, attributeDataFrontend, readOnlyField, attributeList);

        if (inputField && (
            (attributeDataFrontend.isVisible && attributeDataFrontend.isVisible !== 'N') ||
            (!attributeDataFrontend.isVisible && attributeDataBackend.isVisible)
        )) {
            currentRow.push(
                <div key={attributeDataBackend.field} id='attribute-control'>
                    <span className='span-role-control' style={{ marginRight: '5px', display: 'flex', maxHeight: '18px' }}>{getNameField(attributeDataFrontend, attributeDataBackend)}</span>
                    {inputField}
                </div>
            );

            if (attributeDataFrontend.newRow) {
                rows.push(<div key={`row-${rows.length}`} style={row} className="attribute-row">{currentRow}</div>);
                currentRow = [];
            }
        }
    });

    if (currentRow.length > 0) {
        rows.push(<div key={`row-${rows.length}`} style={row} className="attribute-row">{currentRow} </div>);
    }

    return (
        <Panel style={mainStyle} className={'scrollable-content'}>
            {rows}
        </Panel>
    );
};

function getNameField(attributeDataFrontend, attributeDataBackend) {
    const baseText = attributeDataFrontend.customMessage
        ? attributeDataFrontend.customMessage
        : attributeDataBackend.label;

    return (
        <>
            {baseText}
            {((attributeDataBackend.isMandatory === 'S' && attributeDataFrontend.isMandatory !== 'N') || attributeDataFrontend.isMandatory === 'S') && mandatoryField()}
        </>
    );
}

function mandatoryField() {
    return <MandatoryIconField />
}

function getReadOnlyField(readOnlyAttributes, readOnlyFrontend, readOnlyBackend) {
    if (readOnlyFrontend) {
        return true
    } else if (!readOnlyFrontend && readOnlyAttributes) {
        return true;
    } else if (!readOnlyFrontend && !readOnlyAttributes && readOnlyBackend === 'S') {
        return true
    }
    return false
}

function conditionsIsTrue(check, attributeList) {
    const relatedAttribute = attributeList.find(obj => obj.name === check.field) || {};
    const value = relatedAttribute.value;

    if (check.condition === 'NOT_NULL' && !value) {
        return false;
    }

    if (check.condition === 'VALUE_EQUALS' && check.arrayValue.length > 0 && !check.arrayValue.includes(value)) {
        return false;
    }

    return true;
}

function validateIfChecked(checks, attributeList) {
    if (!checks) {
        return false;
    }

    return checks.every(check => conditionsIsTrue(check, attributeList));
}

function getIfChecked(attributeDataFrontend, attributeList) {
    if (attributeDataFrontend.checks) {
        return validateIfChecked(attributeDataFrontend.checks, attributeList);
    }
    return attributeDataFrontend.value === 'S';
}

function getCheckboxSetter(attributeDataFrontend) {
    if (attributeDataFrontend.checkboxSetter) {
        return attributeDataFrontend.checkboxSetter
    } else {
        return (
            function checkboxSetter(value) {
                attributeDataFrontend.setter(value === 'S' ? 'N' : 'S')
            }
        )
    }
}

function convertLocalTimeToInstant(localTime) {
    const isLocalTimeFormat = /^\d{2}:\d{2}:\d{2}$/.test(localTime);

    if (!localTime || !isLocalTimeFormat) {
        return localTime
    }

    const today = new Date();
    const [hours, minutes, seconds] = localTime.split(':').map(Number);
    today.setHours(hours, minutes, seconds, 0);
    return today;
}

const handleSetAttribute = (value, attributeDataBackend, attributeDataFrontend) => {
    if (attributeDataBackend?.dataType?.toUpperCase() === "NUMBER") {
        if (value) {
            value = String(value).replace(",", ".");
        }

        if (!/^\d*\.?\d*$/.test(value)) {
            return;
        }
    }

    if (attributeDataFrontend && attributeDataFrontend.setter) {
        attributeDataFrontend?.setter(value);
    }
};

function getComponentField(attributeDataBackend, attributeDataFrontend, readOnlyField, attributeList) {
    const width = attributeDataFrontend.width ? attributeDataFrontend.width : '150px';
    switch (attributeDataBackend.columnType.toUpperCase()) {
        case 'INPUT':
            return (
                <InputField
                    setAttribute={(value) => handleSetAttribute(value, attributeDataBackend, attributeDataFrontend)}
                    value={attributeDataFrontend.value}
                    readOnly={readOnlyField}
                    disabled={readOnlyField}
                    style={{ ...attributeDataFrontend.style, width: width }}
                    mask={attributeDataBackend.mask}
                    onBlur={attributeDataFrontend.onBlur}
                    focus={attributeDataFrontend.focus}
                    countryCode={attributeDataFrontend.countryCode}
                    dataType={attributeDataBackend.dataType}
                />
            )
        case 'COMBOBOX-SEARCH':
            return (<SearchableComboBox setAttribute={attributeDataFrontend.setter}
                optionsData={attributeDataFrontend.comboboxOptions}
                value={attributeDataFrontend.value}
                description={attributeDataFrontend.description}
                setterDescription={attributeDataFrontend.setterDescription}
                styleInput={{ ...attributeDataFrontend.style, width: width }}
                readOnly={readOnlyField}
                disabled={readOnlyField}
                onBlur={attributeDataFrontend.onBlur}
            />
            )
        case 'COMBOBOX':
            return (
                <ComboBox setAttribute={attributeDataFrontend.setter}
                    optionsData={attributeDataFrontend.comboboxOptions}
                    value={attributeDataFrontend.value}
                    style={{ ...attributeDataFrontend.style, width: width }}
                    firstOptionSelected={attributeDataFrontend.firstOptionSelected}
                    readOnly={readOnlyField}
                    disabled={readOnlyField}
                    onBlur={attributeDataFrontend.onBlur}
                />
            )
        case 'CHECKBOX':
            return (
                <InputField
                    setAttribute={getCheckboxSetter(attributeDataFrontend)}
                    value={attributeDataFrontend.value}
                    checked={getIfChecked(attributeDataFrontend, attributeList)}
                    readOnly={readOnlyField}
                    disabled={readOnlyField}
                    type="checkbox"
                    style={{ ...attributeDataFrontend.style, cursor: 'pointer', width: width }}
                    onBlur={attributeDataFrontend.onBlur}
                />
            )
        case 'TEXT-AREA':
            return (
                <TextAreaField
                    setAttribute={attributeDataFrontend.setter}
                    value={attributeDataFrontend.value}
                    type="text"
                    readOnly={readOnlyField}
                    disabled={readOnlyField}
                    style={{ ...attributeDataFrontend.style, width: width }}
                    onBlur={attributeDataFrontend.onBlur}
                />
            )
        case 'DATE':
            return (
                <ReactDataPickerComp
                    selected={attributeDataFrontend.value}
                    onChange={(date) => { attributeDataFrontend.setter(date) }}
                    className='cost-fix-width-date cursor-pointer'
                    showMonthYearPicker={attributeDataBackend.mask.toUpperCase() === 'MM/YYYY'}
                    showTimeSelect={attributeDataBackend.mask.toUpperCase() === 'HH:MM'}
                    showTimeSelectOnly={attributeDataBackend.mask.toUpperCase() === 'HH:MM'}
                    timeFormat={attributeDataBackend.mask}
                    placeholderText={attributeDataBackend.placeholder}
                    dateFormat={attributeDataBackend.mask}
                    timeCaption='Horas'
                    style={{ ...attributeDataFrontend.style, width: '150px' }}
                    readOnly={readOnlyField}
                    disabled={readOnlyField}
                    onBlur={attributeDataFrontend.onBlur}
                    setter={attributeDataFrontend.setter}
                />
            )
        default:
            return null
    }
}

export default AutomaticallyListedAttributes;
